import { useCallback, useEffect, useRef, useState } from 'react';
import { WizardContent } from '../../components/WizardContent';
import WizardTextField from '../../components/WizardTextField';
import InputMask from 'react-input-mask';
import WizardButton from '../../components/WizardButton';
import { Checkbox } from '@mui/material';
import { StyledFormControlLabel } from './Identification.styles';
import * as yup from 'yup';
import { cpf as validate } from 'cpf-cnpj-validator';
import { useDate } from '../../../../hooks/providers/useDate';
import { useValidateSchema } from '../../../../hooks/providers/useValidateSchema';
import IFormError from '../../../../interfaces/IFormError';
import { useWizardRequest } from '../../../../hooks/services/useWizardRequest';
import { ShowError, StyledAlert } from '../../components/ErrorMessage';
import { WizardTitle } from '../../components/WizardTitle';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker, LocalizationProvider, ptBR } from '@mui/x-date-pickers';
import ILocalSettings from '../../../../interfaces/ILocalSettings';
import { useLocalStorage } from '../../../../hooks/providers/useLocalStorage';
import IWizardStep from '../../../../interfaces/IWizardStep';
import { useSearchParams } from 'react-router-dom';
import { useFocus } from '../../../../hooks/util/useFocus';
import IConfirmationData from '../../../../interfaces/IConfirmationData';
import { useSystemInformationService } from '../../../../hooks/services/useSystemInformationService';
import { useToastrVanila } from '../../../../hooks/util/useToastrVanila';

interface identificationProps extends IWizardStep {}

const Identification = (props: identificationProps) => {
  const [cpf, setCpf] = useState('');
  const [birthday, setBirthday] = useState<string | null>('');
  const [rememberData, setRememberData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<IFormError>();
  const [isError, setIsError] = useState(false);
  const [localSettings, setLocalSettings] = useState<ILocalSettings>({});
  const validationProvader = useValidateSchema();
  const wizardService = useWizardRequest();
  const localStorage = useLocalStorage();
  const [searchParams] = useSearchParams();
  const useDateProvider = useDate();
  const birthDateRef = useRef<HTMLInputElement>();
  const rememberRef = useRef<HTMLInputElement>();
  const buttonRef = useRef<HTMLInputElement>();
  const focus = useFocus();
  const localStograge = useLocalStorage();
  const request = useSystemInformationService();

  const toastr = useToastrVanila();

  const schema = yup.object({
    cpf: yup.string().test((cpf, schema) => cpfValidation(`${cpf}`, schema)),
    birthday: yup
      .string()
      .nullable(true)
      .test((date, schema) => dateValidation(`${date}`, schema)),
  });

  const dateValidation = (date: string, schema: yup.TestContext): yup.ValidationError | boolean => {
    if (!date) {
      return schema.createError({ message: `Campo obrigatório` });
    }
    if (date === 'null') {
      return schema.createError({ message: `Campo obrigatório` });
    }

    if (date === 'Invalid date') {
      return schema.createError({ message: `Data inválida` });
    }
    return true;
  };

  const cpfValidation = (cpf: string, schema: yup.TestContext): yup.ValidationError | boolean => {
    if (!cpf) {
      return schema.createError({ message: `Campo obrigatório` });
    }
    if (!validate.isValid(cpf)) {
      return schema.createError({ message: `CPF Inválido` });
    } else {
      return true;
    }
  };

  const listTimeClocking = useCallback(async () => {
    const tenantDomain = searchParams.get('tenantDomain');

    if (tenantDomain) {
      await request
        .getUseTimeClockingTenant(tenantDomain)
        .then(async result => {
          localStorage.setItem('useTimeClocking', result.useTimeClocking);
          localStorage.setItem('allowsAdjustTimeClock', result.allowsAdjustTimeClock);
        })
        .catch(error => {
          toastr.error(error.message);
        });
    }
  }, [localStorage, request, toastr]);

  const getLocalSettings = () => {
    const localSettingsStorage = localStorage.getItem('localSettings') || {};
    const tenantDomain = localSettingsStorage?.tenantDomain;

    if (searchParams.get('tenantDomain') && !tenantDomain) {
      localSettingsStorage.tenantDomain = searchParams.get('tenantDomain');
      localStorage.setItem('localSettings', { ...localSettingsStorage, tenantDomain });
    }
    setLocalSettings(state => {
      return {
        ...state,
        ...localSettingsStorage,
      };
    });
    if (localSettingsStorage?.rememberData !== undefined) {
      setRememberData(localSettingsStorage?.rememberData);
    }
    if (localSettingsStorage?.rememberData && localSettingsStorage?.birthday && localSettingsStorage?.cpf) {
      setBirthday(localSettingsStorage?.birthday);
      setCpf(localSettingsStorage?.cpf);
    }
  };

  const handleSubmit = async () => {
    const result = await validationProvader.validate(schema, { cpf, birthday });
    setErrors({ ...result.errors });
    if (!result.isValid) {
      return;
    }
    setLoading(true);
    const cpfWithoutMask = `${cpf}`?.replaceAll('.', '')?.replaceAll('-', '');

    await wizardService
      .listByCpfAndBirthday({ cpf: `${cpfWithoutMask}`, birthday: useDateProvider.format(`${birthday}`, 'yyyy-MM-DD') })
      .then(async employee => {
        const confirmationData: IConfirmationData = {
          companyName: `${employee?.company?.name}`,
          employeeName: `${employee?.name}`,
        };
        localStograge.setItem('confirmationData', confirmationData);
        setLoading(false);
        localSettings.rememberData = rememberData;
        localSettings.cpf = cpf;
        localSettings.birthday = birthday;
        const savadLocalSettings = localStorage.getItem('localSettings');

        localStorage.setItem('localSettings', { ...savadLocalSettings, ...localSettings });

        await props.setStep(props.step + 1, { ...props.medicalCertificate, employeeId: employee?.id || 0 });
      })
      .catch(() => {
        setLoading(false);
        setIsError(true);
      });
  };

  useEffect(() => {
    const localData = localStorage.getItem('localSettings');
    getLocalSettings();
    if (localData && localData.rememberData === false) {
      setRememberData(localData.rememberData);
      localStorage.setItem('localSettings', {
        rememberData: localData.rememberData,
      });
    }

    listTimeClocking();
  }, []);

  return (
    <WizardContent>
      <WizardTitle>Informe seus dados</WizardTitle>
      <form>
        <InputMask
          mask="999.999.999-99"
          value={cpf}
          onChange={e => {
            setCpf(e.target.value);
            setIsError(false);
          }}
          maskPlaceholder={null}
          style={{ marginBottom: '20px' }}
        >
          <WizardTextField
            label="CPF"
            placeholder="CPF"
            variant="outlined"
            sx={{ width: '300px' }}
            error={Boolean(errors?.cpf)}
            helperText={errors?.cpf}
            autoFocus
            onKeyUpCapture={e => {
              focus.setFocus(e, birthDateRef);
            }}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
          />
        </InputMask>
        <LocalizationProvider
          dateAdapter={AdapterMoment}
          localeText={ptBR.components.MuiLocalizationProvider.defaultProps.localeText}
        >
          <DatePicker
            value={birthday}
            label="Nascimento"
            onChange={e => {
              setBirthday(e);
              setIsError(false);
            }}
            renderInput={params => (
              <WizardTextField
                {...params}
                size="small"
                sx={{ width: '300px', maxWidth: '100%' }}
                error={Boolean(errors?.birthday)}
                placeholder="Data de Nascimento"
                helperText={errors?.birthday}
                onKeyUpCapture={e => {
                  focus.setFocus(e, rememberRef);
                }}
              />
            )}
          />
        </LocalizationProvider>
        <StyledFormControlLabel
          label="Lembrar meus dados"
          inputRef={rememberRef}
          control={
            <Checkbox
              onKeyUpCapture={e => {
                focus.setFocus(e, buttonRef);
              }}
              onChange={e => setRememberData(e.target.checked)}
              checked={rememberData}
              sx={{
                color: 'rgb(32, 144, 136)',
                '&.Mui-checked': {
                  color: 'rgb(32, 144, 136)',
                },
              }}
            />
          }
        />

        <div>
          <ShowError show={isError}>
            <StyledAlert severity="error">
              Ops, não conseguimos localizar o colaborador com os dados informados. Entre em contato com o RH ou tente
              novamente.
            </StyledAlert>
          </ShowError>
        </div>

        <WizardButton
          isLoading={loading}
          disabled={loading}
          sx={{ marginTop: '5px' }}
          onClick={handleSubmit}
          ref={buttonRef}
        >
          PRÓXIMO
        </WizardButton>
      </form>
    </WizardContent>
  );
};

export default Identification;
